import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { useModalBackClose } from "../../hooks/useModalBackClose";

/**
 * The drawer primitive: a panel anchored to the bottom edge on small screens.
 *
 * This design language has exactly one drawer — the navigation overflow sheet.
 * Dialogs stay centred (see Modal); a sheet is used only where the content is a
 * list of destinations rather than a task, because a sheet puts its content
 * within thumb reach at the bottom of the screen.
 *
 * The entrance is a 6px lift and a fade, not a full-height slide: a panel
 * travelling the height of the screen reads as slow no matter how short the
 * duration.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  children,
  closeLabel = "Close",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  closeLabel?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useModalBackClose(open, onClose);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end bg-ink-950/50 md:hidden"
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        // The bottom padding clears the home indicator on a gesture-navigation
        // phone, where the last row would otherwise sit under it.
        className="w-full animate-slide-up rounded-t-2xl bg-card p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] outline-none"
      >
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-ink-900">{title}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="-m-1 flex size-10 items-center justify-center rounded-lg text-muted-subtle transition-colors hover:bg-ink-100 hover:text-ink-700"
          >
            <X className="size-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
